import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';

import {Button, BUTTON_VARIANT} from '@lookiero/aurora';
import {MountableModal, OptionSelector} from '@components/molecules';
import {MODALITY, Modality} from '@domain/model/Account/Subscription/Subscription';

import {TEXT, TEXT_DESCRIPTION} from './ModalRecoveryPlan.definition';
import {style} from './ModalRecoveryPlan.styles';

type ModalRecoveryPlanProps = {
  modality: Modality;
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (modality: Modality) => void;
  busy?: boolean;
};

export const ModalRecoveryPlan: React.FC<ModalRecoveryPlanProps> = ({
  modality,
  isVisible,
  onClose,
  onSubmit,
  busy = false,
}) => {
  const {formatMessage} = useIntl();

  const [optionSelected, setOptionSelected] = useState<string>();

  useEffect(() => {
    if (isVisible) setOptionSelected(undefined);
  }, [isVisible]);

  const options = useMemo(
    () => [
      {
        id: MODALITY.BIMONTHLY,
        title: formatMessage(TEXT.MODALITY_BIMONTHLY_TITLE),
        label: formatMessage(TEXT.MOST_POPULAR),
      },
      {
        id: MODALITY.QUARTERLY,
        title: formatMessage(TEXT.MODALITY_QUARTERLY_TITLE),
      },
    ],
    [formatMessage],
  );

  const visibleOptions = useMemo(() => {
    const optionIndex = options.findIndex(({id}) => id === modality);
    return options.filter((item, index) => index > optionIndex);
  }, [options, modality]);

  const handleUpdatePlan = (): void => {
    if (optionSelected) onSubmit(optionSelected as Modality);
  };

  const handleCancelPlan = (): void => {
    onSubmit(MODALITY.ON_DEMAND);
  };

  const hasOptionsToChoose = Boolean(visibleOptions.length);

  return (
    <MountableModal
      portalID="modal-recovery-plan"
      isVisible={isVisible}
      onClose={onClose}
      title={formatMessage(TEXT.TITLE)}
      description={formatMessage(TEXT_DESCRIPTION[modality])}
    >
      {hasOptionsToChoose && (
        <>
          <OptionSelector
            style={style.options}
            options={visibleOptions}
            selected={optionSelected}
            onSelected={setOptionSelected}
            disableGrayOutOnBlur
          />
          <Button onPress={handleUpdatePlan} disabled={optionSelected === undefined} busy={busy}>
            {formatMessage(TEXT.CTA_UPDATE_PLAN)}
          </Button>
          <Button
            onPress={handleCancelPlan}
            variant={BUTTON_VARIANT.SECONDARY}
            style={style.secondaryButton}
            busy={busy}
          >
            {formatMessage(TEXT.CTA_CANCEL_PLAN)}
          </Button>
        </>
      )}
      {!hasOptionsToChoose && (
        <>
          <Button onPress={handleCancelPlan} busy={busy}>
            {formatMessage(TEXT.CTA_CANCEL_PLAN)}
          </Button>
          <Button onPress={onClose} variant={BUTTON_VARIANT.SECONDARY} style={style.secondaryButton}>
            {formatMessage(TEXT.CTA_CLOSE)}
          </Button>
        </>
      )}
    </MountableModal>
  );
};
